/**
 * Account Info Component
 */

import { useState } from 'react';
import { accountApi } from '@/api/client';
import { useAuth, useShop } from '@/context';
import { Button } from '../shared/Button';
import styles from './AccountInfo.module.css';

export function AccountInfo() {
  const { user, refreshUser } = useAuth();
  const { t } = useShop();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    display_name: user?.display_name || '',
    email: user?.email || '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      await accountApi.updateAccount({
        first_name: formData.first_name,
        last_name: formData.last_name,
        display_name: formData.display_name,
      });

      await refreshUser();
      setIsEditing(false);
      setSuccess('Informations mises à jour avec succès');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      display_name: user?.display_name || '',
      email: user?.email || '',
    });
    setIsEditing(false);
    setError(null);
  };

  if (!user) return null;

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('myAccount')}</h2>

      {error && <div className={styles.error}>{error}</div>}
      {success && <div className={styles.success}>{success}</div>}

      {isEditing ? (
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.row}>
            <div className={styles.field}>
              <label htmlFor="first_name" className={styles.label}>
                {t('firstName')}
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className={styles.input}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="last_name" className={styles.label}>
                {t('lastName')}
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className={styles.input}
              />
            </div>
          </div>

          <div className={styles.field}>
            <label htmlFor="display_name" className={styles.label}>
              Nom d'affichage
            </label>
            <input
              type="text"
              id="display_name"
              name="display_name"
              value={formData.display_name}
              onChange={handleChange}
              className={styles.input}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="email" className={styles.label}>
              {t('email')}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              disabled
              className={`${styles.input} ${styles.disabled}`}
            />
            <span className={styles.hint}>
              L'email ne peut pas être modifié
            </span>
          </div>

          <div className={styles.actions}>
            <Button variant="outline" type="button" onClick={handleCancel}>
              {t('cancel')}
            </Button>
            <Button variant="primary" type="submit" isLoading={isSaving}>
              {t('save')}
            </Button>
          </div>
        </form>
      ) : (
        <div className={styles.info}>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Nom</span>
            <span className={styles.infoValue}>
              {user.first_name} {user.last_name}
            </span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Nom d'affichage</span>
            <span className={styles.infoValue}>{user.display_name}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Email</span>
            <span className={styles.infoValue}>{user.email}</span>
          </div>

          <Button variant="outline" onClick={() => setIsEditing(true)}>
            {t('edit')}
          </Button>
        </div>
      )}
    </div>
  );
}
